// src/triggers/operators.ts
var TriggerOperator = {
  EQ: "==",
  NE: "!=",
  GTE: ">=",
  LTE: "<=",
  GT: ">",
  LT: "<",
  IN: "in",
  NOT_IN: "not_in",
  MATCHES: "matches",
  EXISTS: "exists"
};
var TRIGGER_OPERATORS = [
  TriggerOperator.EQ,
  TriggerOperator.NE,
  TriggerOperator.GT,
  TriggerOperator.GTE,
  TriggerOperator.LT,
  TriggerOperator.LTE,
  TriggerOperator.IN,
  TriggerOperator.NOT_IN,
  TriggerOperator.MATCHES,
  TriggerOperator.EXISTS
];
var TRIGGER_OPERATOR_LABELS = {
  "==": "equals",
  "!=": "not equals",
  ">": "greater than",
  ">=": "greater or equal",
  "<": "less than",
  "<=": "less or equal",
  in: "in list",
  not_in: "not in list",
  matches: "matches regex",
  exists: "exists"
};
var UNARY_TRIGGER_OPERATORS = /* @__PURE__ */ new Set([
  TriggerOperator.EXISTS
]);
var LIST_TRIGGER_OPERATORS = /* @__PURE__ */ new Set([
  TriggerOperator.IN,
  TriggerOperator.NOT_IN
]);
var OPERATOR_ALIASES = {
  eq: TriggerOperator.EQ,
  ne: TriggerOperator.NE,
  neq: TriggerOperator.NE,
  gt: TriggerOperator.GT,
  gte: TriggerOperator.GTE,
  lt: TriggerOperator.LT,
  lte: TriggerOperator.LTE,
  in: TriggerOperator.IN,
  not_in: TriggerOperator.NOT_IN,
  nin: TriggerOperator.NOT_IN,
  exists: TriggerOperator.EXISTS,
  regex: TriggerOperator.MATCHES,
  matches: TriggerOperator.MATCHES
};
function normalizeOperator(op) {
  if (TRIGGER_OPERATORS.includes(op)) return op;
  return OPERATOR_ALIASES[op];
}
function isUnaryOperator(op) {
  return UNARY_TRIGGER_OPERATORS.has(op);
}
function isListOperator(op) {
  return LIST_TRIGGER_OPERATORS.has(op);
}
function triggerOperatorLabel(op) {
  return TRIGGER_OPERATOR_LABELS[op] ?? op;
}

// src/display/index.ts
var FIELD_LABELS = {
  // transaction
  canonical_tx_id: "Transaction ID",
  tx_hash: "Tx Hash",
  chain_id: "Chain",
  block_number: "Block",
  block_hash: "Block Hash",
  block_timestamp: "Time",
  transaction_index: "Index",
  type: "Type",
  finality: "Finality",
  direction: "Direction",
  status: "Status",
  total_value_raw: "Value",
  value_usd: "Value (USD)",
  primary_entity_id: "Entity",
  from: "From",
  to: "To",
  counts: "Counts",
  created_at: "Created",
  updated_at: "Updated",
  // canonical-tx attr leaves (shared with `@authe/fe-commons/triggers`, which
  // resolves its field-path labels through `fieldLabel` so the rule builder and
  // the transaction table can never disagree on a name).
  from_address: "From",
  to_address: "To",
  value: "Value (native)",
  input_data: "Input Data",
  total_usd: "Value (USD)",
  // enrichment payload leaves (trigger DSL `<kind>.<path>`)
  score: "Risk Score",
  bucket: "Risk Bucket",
  policy_id: "Risk Policy",
  catalog_id: "Catalog ID",
  kind: "Kind",
  // address / catalog
  address: "Address",
  label: "Label",
  tag: "Tag",
  entity: "Entity",
  group: "Group",
  risk_score: "Risk",
  // catalog rows (entities / tags / labels / groups — canonical commons fields)
  name: "Name",
  description: "Description",
  entity_type: "Type",
  risk_level: "Risk Level",
  website: "Website",
  twitter: "Twitter",
  category: "Category",
  color: "Colour",
  icon: "Icon",
  logo_s3_uri: "Logo",
  lifecycle: "Lifecycle",
  severity: "Severity",
  aliases: "Aliases",
  chain: "Chain",
  parent_entity_id: "Parent Entity",
  related_entity_ids: "Related Entities",
  member_tag_ids: "Tags",
  member_label_ids: "Labels",
  member_entity_ids: "Entities",
  system_default: "System Default",
  confidence: "Confidence",
  explanation: "Why",
  source: "Source",
  is_human_approved: "Approved",
  // tenancy
  org_id: "Organization",
  tenant_id: "Tenant",
  customer_id: "Customer",
  user_id: "User"
};
function titleCase(key) {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
function fieldLabel(key) {
  return FIELD_LABELS[key] ?? titleCase(key);
}

// src/triggers/fields.ts
var FINALITY_STATES = ["observed", "confirmed", "finalized", "reorged"];
var EVENT_KINDS = ["log", "internal_tx", "native_transfer"];
var ENRICHMENT_KINDS = [
  "risk_score",
  "tx_value_usd",
  "price",
  "label",
  "tag",
  "entity",
  "group",
  "classification"
];
var RISK_BUCKETS = ["none", "low", "medium", "high", "critical"];
var O = TriggerOperator;
var STRING_OPS = [O.EQ, O.NE, O.MATCHES, O.IN, O.NOT_IN, O.EXISTS];
var ENUM_OPS = [O.EQ, O.NE, O.IN, O.NOT_IN, O.EXISTS];
var NUMBER_OPS = [O.EQ, O.NE, O.GT, O.GTE, O.LT, O.LTE, O.IN, O.NOT_IN, O.EXISTS];
var TIME_OPS = [O.EQ, O.NE, O.GT, O.GTE, O.LT, O.LTE, O.EXISTS];
var BOOL_OPS = [O.EQ, O.NE, O.EXISTS];
var OPERATORS_BY_TYPE = {
  string: STRING_OPS,
  hash: STRING_OPS,
  address: STRING_OPS,
  enum: ENUM_OPS,
  number: NUMBER_OPS,
  timestamp: TIME_OPS,
  boolean: BOOL_OPS
};
function f(path, group, valueType, label, extra) {
  return {
    path,
    group,
    valueType,
    label,
    operators: extra?.operators ?? OPERATORS_BY_TYPE[valueType],
    enumValues: extra?.enumValues,
    description: extra?.description
  };
}
var TRIGGER_FIELDS = [
  // --- canonical transaction (RuleEvaluator `canonical.<attr>`) -----------
  f("canonical.chain_id", "transaction", "string", fieldLabel("chain_id")),
  f("canonical.tx_hash", "transaction", "hash", fieldLabel("tx_hash")),
  f("canonical.value", "transaction", "number", "Value (native)", {
    description: "Native value as a decimal string."
  }),
  f("canonical.value_usd", "transaction", "number", fieldLabel("value_usd"), {
    description: "Pulled from the tx_value_usd enrichment's total_usd."
  }),
  f("canonical.finality", "transaction", "enum", fieldLabel("finality"), {
    enumValues: FINALITY_STATES
  }),
  f("canonical.block_number", "transaction", "number", fieldLabel("block_number")),
  f("canonical.block_timestamp", "transaction", "timestamp", fieldLabel("block_timestamp")),
  f("canonical.input_data", "transaction", "string", "Input Data"),
  // --- events (RuleEvaluator `event.<attr>`) ------------------------------
  f("event.kind", "event", "enum", "Event Kind", { enumValues: EVENT_KINDS }),
  // --- any-enrichment matcher (RuleEvaluator `enrichment.kind`) -----------
  f("enrichment.kind", "enrichment", "enum", "Enrichment Kind", {
    enumValues: ENRICHMENT_KINDS,
    description: "Matches when any enrichment of this kind is present."
  }),
  // --- risk (`risk_score.<path>`) ----------------------------------------
  f("risk_score.score", "risk", "number", "Risk Score", {
    description: "0\u2013100 weighted-sum score from the risk engine."
  }),
  f("risk_score.bucket", "risk", "enum", "Risk Bucket", { enumValues: RISK_BUCKETS }),
  f("risk_score.policy_id", "risk", "string", "Risk Policy"),
  // --- value (`tx_value_usd` / `price` enrichments) -----------------------
  f("tx_value_usd.total_usd", "enrichment", "number", "Value (USD)"),
  f("price.value_usd", "enrichment", "number", "Price (USD)"),
  // --- catalog hits (`<kind>.catalog_id`) ---------------------------------
  f("tag.catalog_id", "catalog", "string", "Tag (catalog id)"),
  f("label.catalog_id", "catalog", "string", "Label (catalog id)"),
  f("entity.catalog_id", "catalog", "string", "Entity (catalog id)"),
  f("group.catalog_id", "catalog", "string", "Group (catalog id)")
];
var TRIGGER_FIELD_BY_PATH = new Map(
  TRIGGER_FIELDS.map((field) => [field.path, field])
);
var TRIGGER_FIELD_GROUP_LABELS = {
  transaction: "Transaction",
  risk: "Risk",
  enrichment: "Enrichment",
  event: "Event",
  catalog: "Catalog"
};
function getTriggerField(path) {
  return TRIGGER_FIELD_BY_PATH.get(path);
}
function triggerFieldLabel(path) {
  const field = TRIGGER_FIELD_BY_PATH.get(path);
  if (field) return field.label;
  const leaf = path.includes(".") ? path.slice(path.lastIndexOf(".") + 1) : path;
  return fieldLabel(leaf);
}
function operatorsForField(path) {
  return getTriggerField(path)?.operators ?? OPERATORS_BY_TYPE.string;
}
function triggerFieldOptions() {
  const order = ["transaction", "risk", "enrichment", "event", "catalog"];
  return order.map((group) => ({
    group,
    label: TRIGGER_FIELD_GROUP_LABELS[group],
    fields: TRIGGER_FIELDS.filter((field) => field.group === group)
  })).filter((entry) => entry.fields.length > 0);
}

// src/triggers/envelope.ts
function collectEnrichments(envelope, opts) {
  const out = [];
  for (const layer of envelope.enrichment_layers ?? []) {
    for (const e of layer.enrichments) {
      if (opts?.kind != null && e.kind !== opts.kind) continue;
      if (opts?.scope != null && String(e.payload?.scope) !== opts.scope) continue;
      out.push(e);
    }
  }
  return out;
}

// src/triggers/rule.ts
var LogicalOp = {
  AND: "and",
  OR: "or",
  NOT: "not"
};
function predicate(field, op, value) {
  return { field, op, value };
}
function predicateLeaf(p) {
  return { predicate: p };
}
function and(...children) {
  return { op: LogicalOp.AND, children };
}
function or(...children) {
  return { op: LogicalOp.OR, children };
}
function not(...children) {
  return { op: LogicalOp.NOT, children };
}
function anyCondition() {
  return {};
}
function conditionFromPredicates(predicates, match) {
  if (predicates.length === 0) return anyCondition();
  if (predicates.length === 1) return predicateLeaf(predicates[0]);
  const children = predicates.map(predicateLeaf);
  return match === "all" ? and(...children) : or(...children);
}
function flattenCondition(condition) {
  if (!condition || condition.op == null && condition.predicate == null) {
    return { predicates: [], match: "all" };
  }
  if (condition.predicate != null) {
    return { predicates: [condition.predicate], match: "all" };
  }
  if (condition.op === LogicalOp.AND || condition.op === LogicalOp.OR) {
    const children = condition.children ?? [];
    const predicates = [];
    for (const child of children) {
      if (child.predicate == null) return null;
      predicates.push(child.predicate);
    }
    return { predicates, match: condition.op === LogicalOp.AND ? "all" : "any" };
  }
  return null;
}
function emptyTriggerRule(id) {
  return {
    id,
    name: "",
    enabled: true,
    condition: anyCondition(),
    actions: [],
    cooldown_seconds: 0,
    metadata: {}
  };
}

// src/triggers/actions.ts
var TriggerActionType = {
  NOTIFY: "notify",
  START_WORKFLOW: "start_workflow",
  INTEGRATION: "integration"
};
var TRIGGER_ACTION_TYPES = [
  TriggerActionType.NOTIFY,
  TriggerActionType.START_WORKFLOW,
  TriggerActionType.INTEGRATION
];
var TRIGGER_ACTION_TYPE_LABELS = {
  notify: "Alert / notify",
  start_workflow: "Start workflow",
  integration: "Integration"
};
function notifyAction(destinationId) {
  return { type: TriggerActionType.NOTIFY, destination_id: destinationId };
}
function startWorkflowAction(workflowId, input) {
  return {
    type: TriggerActionType.START_WORKFLOW,
    workflow_id: workflowId,
    ...input ? { input } : {}
  };
}
function integrationAction(connectorId, config) {
  return {
    type: TriggerActionType.INTEGRATION,
    connector_id: connectorId,
    ...config ? { config } : {}
  };
}
function isNotifyAction(a) {
  return a.type === TriggerActionType.NOTIFY;
}
function isStartWorkflowAction(a) {
  return a.type === TriggerActionType.START_WORKFLOW;
}
function isIntegrationAction(a) {
  return a.type === TriggerActionType.INTEGRATION;
}
function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function parseTriggerAction(record) {
  const type = typeof record.type === "string" ? record.type : void 0;
  if (type === TriggerActionType.NOTIFY || type === "destination") {
    return typeof record.destination_id === "string" ? { type: TriggerActionType.NOTIFY, destination_id: record.destination_id } : null;
  }
  if (type === TriggerActionType.START_WORKFLOW) {
    if (typeof record.workflow_id !== "string") return null;
    const action = {
      type: TriggerActionType.START_WORKFLOW,
      workflow_id: record.workflow_id
    };
    const input = asRecord(record.input);
    if (input) action.input = input;
    if (record.mode === "async" || record.mode === "sync") action.mode = record.mode;
    return action;
  }
  if (type === TriggerActionType.INTEGRATION) {
    if (typeof record.connector_id !== "string") return null;
    const action = {
      type: TriggerActionType.INTEGRATION,
      connector_id: record.connector_id
    };
    const config = asRecord(record.config);
    if (config) action.config = config;
    return action;
  }
  return null;
}
function triggerActionToRecord(action) {
  return { ...action };
}
var DEFAULT_CONNECTORS = [
  {
    id: "http",
    name: "Generic HTTP",
    description: "POST the matched event to a customer endpoint.",
    configFields: [{ key: "url", label: "Endpoint URL", type: "string", required: true }]
  },
  { id: "pagerduty", name: "PagerDuty", description: "Open a PagerDuty incident." },
  { id: "zapier", name: "Zapier", description: "Kick off a Zapier zap via catch hook." }
];
function getConnector(id) {
  return DEFAULT_CONNECTORS.find((c) => c.id === id);
}

// src/triggers/evaluate.ts
var EvalResult = class {
  matchedEnrichments = [];
  add(e) {
    const record = {
      kind: e.kind,
      source: e.source,
      payload: { ...e.payload }
    };
    const key = JSON.stringify(record);
    if (!this.matchedEnrichments.some((m) => JSON.stringify(m) === key)) {
      this.matchedEnrichments.push(record);
    }
  }
};
var FIELD_AT_SCOPE_RE = /^([a-zA-Z_]+)@([a-zA-Z_]+)(?:\.(.+))?$/;
function toFloat(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function pyEq(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === "boolean" || typeof b === "boolean") {
    if (typeof a === "string" || typeof b === "string") return false;
    return Number(a) === Number(b);
  }
  if (typeof a === "number" && typeof b === "number") return a === b;
  if (typeof a === "string" && typeof b === "string") return a === b;
  if (typeof a !== typeof b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
function membership(actual, expected) {
  if (Array.isArray(expected)) return expected.some((item) => pyEq(actual, item));
  if (typeof expected === "string" && typeof actual === "string") {
    return expected.includes(actual);
  }
  return false;
}
function compare(actual, op, expected) {
  switch (op) {
    case TriggerOperator.EXISTS:
      return actual !== void 0 && actual !== null;
    case TriggerOperator.EQ:
      return pyEq(actual, expected);
    case TriggerOperator.NE:
      return !pyEq(actual, expected);
    case TriggerOperator.IN:
      return membership(actual, expected);
    case TriggerOperator.NOT_IN:
      if (!Array.isArray(expected) && typeof expected !== "string") return true;
      return !membership(actual, expected);
    case TriggerOperator.MATCHES: {
      if (actual == null || typeof expected !== "string") return false;
      try {
        return new RegExp(expected).test(String(actual));
      } catch {
        return false;
      }
    }
    case TriggerOperator.GTE:
    case TriggerOperator.LTE:
    case TriggerOperator.GT:
    case TriggerOperator.LT: {
      const a = toFloat(actual);
      const b = toFloat(expected);
      if (a === null || b === null) return false;
      if (op === TriggerOperator.GTE) return a >= b;
      if (op === TriggerOperator.LTE) return a <= b;
      if (op === TriggerOperator.GT) return a > b;
      return a < b;
    }
    default:
      return false;
  }
}
function getCanonicalField(envelope, attr) {
  const canonical = envelope.canonical;
  if (canonical == null) return void 0;
  if (attr === "value_usd") {
    for (const e of collectEnrichments(envelope, { kind: "tx_value_usd" })) {
      return e.payload?.total_usd;
    }
    return void 0;
  }
  return canonical[attr];
}
function getPayloadPath(payload, path) {
  let actual = payload;
  for (const segment of path.split(".")) {
    if (actual != null && typeof actual === "object" && !Array.isArray(actual)) {
      actual = actual[segment];
    } else {
      return void 0;
    }
  }
  return actual;
}
function evalPredicate(predicate2, envelope, result) {
  const { field, op, value } = predicate2;
  if (field.startsWith("canonical.")) {
    const attr = field.slice("canonical.".length);
    return compare(getCanonicalField(envelope, attr), op, value);
  }
  if (field.startsWith("event.")) {
    const attr = field.slice("event.".length);
    for (const ev of envelope.canonical?.events ?? []) {
      if (compare(ev[attr], op, value)) return true;
    }
    return false;
  }
  const at = FIELD_AT_SCOPE_RE.exec(field);
  if (at) {
    const [, kind, scope, rest] = at;
    for (const e of collectEnrichments(envelope, { kind, scope })) {
      if (rest == null) {
        result.add(e);
        return true;
      }
      if (compare(getPayloadPath(e.payload, rest), op, value)) {
        result.add(e);
        return true;
      }
    }
    return false;
  }
  if (field.includes(".")) {
    const idx = field.indexOf(".");
    const kind = field.slice(0, idx);
    const rest = field.slice(idx + 1);
    if (rest === "kind") {
      for (const e of collectEnrichments(envelope)) {
        if (compare(e.kind, op, value)) {
          result.add(e);
          return true;
        }
      }
      return false;
    }
    for (const e of collectEnrichments(envelope, { kind })) {
      if (compare(getPayloadPath(e.payload, rest), op, value)) {
        result.add(e);
        return true;
      }
    }
    return false;
  }
  return false;
}
function evalCondition(condition, envelope, result) {
  if (condition.predicate != null) {
    return evalPredicate(condition.predicate, envelope, result);
  }
  if (condition.op == null) {
    return true;
  }
  const children = condition.children ?? [];
  switch (condition.op) {
    case "and":
      return children.every((c) => evalCondition(c, envelope, result));
    case "or":
      return children.some((c) => evalCondition(c, envelope, result));
    case "not":
      return !children.some((c) => evalCondition(c, envelope, result));
    default:
      return false;
  }
}
function evaluatePredicate(predicate2, envelope) {
  return evalPredicate(predicate2, envelope, new EvalResult());
}
function evaluateCondition(condition, envelope) {
  return evalCondition(condition, envelope, new EvalResult());
}
function evaluateRule(rule, envelope) {
  const condition = "condition" in rule ? rule.condition : rule;
  const result = new EvalResult();
  const matched = evalCondition(condition, envelope, result);
  return { matched, matchedEnrichments: result.matchedEnrichments };
}
function previewRule(rule, envelopes, limit) {
  const matches = [];
  for (const envelope of envelopes) {
    const evaluation = evaluateRule(rule, envelope);
    if (evaluation.matched) matches.push({ envelope, evaluation });
    if (limit && matches.length >= limit) break;
  }
  return matches;
}

export { DEFAULT_CONNECTORS, ENRICHMENT_KINDS, EVENT_KINDS, FINALITY_STATES, LIST_TRIGGER_OPERATORS, LogicalOp, OPERATORS_BY_TYPE, OPERATOR_ALIASES, RISK_BUCKETS, TRIGGER_ACTION_TYPES, TRIGGER_ACTION_TYPE_LABELS, TRIGGER_FIELDS, TRIGGER_FIELD_GROUP_LABELS, TRIGGER_OPERATORS, TRIGGER_OPERATOR_LABELS, TriggerActionType, TriggerOperator, UNARY_TRIGGER_OPERATORS, and, anyCondition, collectEnrichments, compare, conditionFromPredicates, emptyTriggerRule, evaluateCondition, evaluatePredicate, evaluateRule, flattenCondition, getCanonicalField, getConnector, getTriggerField, integrationAction, isIntegrationAction, isListOperator, isNotifyAction, isStartWorkflowAction, isUnaryOperator, normalizeOperator, not, notifyAction, operatorsForField, or, parseTriggerAction, predicate, predicateLeaf, previewRule, startWorkflowAction, triggerActionToRecord, triggerFieldLabel, triggerFieldOptions, triggerOperatorLabel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map